import { getUSStateName } from 'utils/restaurant/get-us-state';

type PhysicalAddressLike = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  stateProvince?: string | null;
  stateProvinceShort?: string | null;
};

/**
 * Single-line mailing-style address for display and screen-reader labels.
 */
export const formatRestaurantPhysicalAddress = (
  physicalAddress: PhysicalAddressLike | null | undefined
): string => {
  if (!physicalAddress) {
    return '';
  }

  const line1 = [physicalAddress.address1, physicalAddress.address2]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(', ');

  const state = (physicalAddress.stateProvinceShort || physicalAddress.stateProvince)?.trim();
  const cityLocality = physicalAddress.city?.trim();
  const zip = physicalAddress.postalCode?.trim();
  const cityStateZip = [cityLocality, [state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [line1, cityStateZip].filter(Boolean).join(', ');
};

export const formatRestaurantPhysicalAddressForAccessibility = (
  physicalAddress: PhysicalAddressLike | null | undefined
): string => formatRestaurantPhysicalAddressWithStateFormatter(physicalAddress, getStateName);

const formatRestaurantPhysicalAddressWithStateFormatter = (
  physicalAddress: PhysicalAddressLike | null | undefined,
  formatState: (state: string | null | undefined) => string
): string => {
  if (!physicalAddress) {
    return '';
  }

  const line1 = [physicalAddress.address1, physicalAddress.address2]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(', ');

  const state = formatState(physicalAddress.stateProvince || physicalAddress.stateProvinceShort);
  const cityLocality = physicalAddress.city?.trim();
  const zip = physicalAddress.postalCode?.trim();
  const cityStateZip = [cityLocality, [state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [line1, cityStateZip].filter(Boolean).join(', ');
};

const getStateName = (state: string | null | undefined): string => {
  const trimmed = state?.trim();
  if (!trimmed) {
    return '';
  }

  return getUSStateName(trimmed);
};
